/**
 * Growth Acquisition.
 *
 * The scorecard puts the three bases side by side and never sums them, because the
 * disagreement is the information. Blended ROAS is bank truth. Modelled ROAS is
 * ThoughtMetric's allocation of that same money. The claim ratio is what the platforms say
 * they drove, divided by what the bank actually saw; when it reads above one, the platforms
 * are between them claiming more revenue than the store collected, and the tile says so.
 *
 * The feedback tally and the annotation list live on this page rather than at routes of
 * their own, because API.md gives Growth nine routes and they ride on this one.
 */
import { useState } from 'react';
import type { WindowKind } from '@elutide/shared';
import {
  Card,
  EmptyState,
  ErrorState,
  KpiTile,
  MoneyText,
  Pill,
  Skeleton,
  DataTable,
  type Column,
} from '@/components';
import { formatMoney, formatPercent } from '@/lib/format';
import { WindowChips } from '@/features/finance/Overview';
import { useAcquisition } from './api';
import type { Annotation, Freshness, ProductRequest } from './types';
import '@/features/finance/finance.css';

const TILE_LABELS: Record<string, string> = {
  roas_blended: 'Blended ROAS',
  roas_modeled: 'Modeled ROAS',
  claim_ratio: 'Claim ratio',
  cac_media: 'CAC, media',
  cac_fully_loaded: 'CAC, fully loaded',
  new_customers: 'New customers',
  repeat_rate: 'Repeat rate',
};

const TILE_NOTES: Record<string, string> = {
  roas_blended: 'revenue net over paid media spend',
  roas_modeled: 'ThoughtMetric attribution',
  claim_ratio: 'platform claimed over bank truth',
  cac_media: 'ad spend over new customers',
  cac_fully_loaded: 'plus partner CPA, rewards and first order discounts',
};

export function GrowthAcquisition() {
  const [window, setWindow] = useState<WindowKind>('30d');
  const query = useAcquisition(window);

  if (query.isLoading) return <Skeleton shape="card" />;
  if (query.isError || !query.data) {
    return (
      <ErrorState
        message="Acquisition could not be loaded."
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  const data = query.data;
  const split = data.new_vs_returning;
  const shareValue = split.share.value;
  const total = split.new_cents + split.returning_cents;

  return (
    <div className="o-stack">
      <WindowChips value={window} onChange={setWindow} />

      <Card title="Acquisition scorecard" action={<FreshnessStrip sources={data.freshness} />}>
        <div className="o-scorecard">
          {data.metrics.map((envelope) => (
            <KpiTile
              key={envelope.metric_id}
              label={TILE_LABELS[envelope.metric_id] ?? envelope.metric_id.replace(/_/g, ' ')}
              envelope={envelope}
              note={TILE_NOTES[envelope.metric_id] ?? null}
              tone={
                envelope.metric_id === 'claim_ratio' && envelope.value !== null && envelope.value > 1.2
                  ? 'warn'
                  : undefined
              }
            />
          ))}
        </div>
        <p className="o-muted">{data.overlap_note}</p>
      </Card>

      <div className="o-split o-split--even">
        <Card title="New versus returning revenue" basis="woo_paid" window={split.share.window} asOf={split.share.as_of} ageClass="snapshot">
          <div className="o-kv">
            <span className="o-kv__label">New customers</span>
            <span className="o-kv__value">
              <MoneyText cents={split.new_cents} showCents={false} />
            </span>
          </div>
          <div className="o-kv">
            <span className="o-kv__label">Returning customers</span>
            <span className="o-kv__value">
              <MoneyText cents={split.returning_cents} showCents={false} />
            </span>
          </div>
          <div className="o-kv o-kv--total">
            <span className="o-kv__label">New customer share</span>
            <span className="o-kv__value">
              {formatPercent(shareValue)}{' '}
              <Pill
                tone={split.band_state === 'in_band' ? 'ok' : split.band_state === 'unknown' ? 'neutral' : 'warn'}
              >
                {split.band_state === 'low'
                  ? 'below 30 percent'
                  : split.band_state === 'high'
                    ? 'above 60 percent'
                    : split.band_state === 'unknown'
                      ? 'no data'
                      : 'in band'}
              </Pill>
            </span>
          </div>
          <p className="o-muted">
            The target band is 30 to 60 percent. Below it, acquisition has stalled and the business
            is living on the customers it already has. Above it, retention is leaking and every new
            customer is replacing one that left. Total revenue in the window:{' '}
            {formatMoney(total, { showCents: false })}.
          </p>
        </Card>

        <FeedbackCard windowDays={data.feedback.window_days} requests={data.feedback.requests} />
      </div>

      <AnnotationList annotations={data.annotations} />
    </div>
  );
}

/* ------------------------------------------------------------------ */

export function FreshnessStrip({ sources }: { sources: Freshness[] }) {
  if (sources.length === 0) return null;
  return (
    <span style={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap' }}>
      {sources.map((source) => (
        <Pill
          key={source.source}
          tone={source.state === 'fresh' ? 'ok' : source.state === 'stale' ? 'warn' : 'neutral'}
          title={
            source.state === 'unknown'
              ? `${source.source} has no credentials configured, so its cards render unknown rather than zero.`
              : (source.last_error ?? `${source.source} last succeeded ${source.as_of ?? 'never'}`)
          }
        >
          {source.source.replace(/_/g, ' ')}
        </Pill>
      ))}
    </span>
  );
}

/* ------------------------------------------------------------------ */

export function FeedbackCard({ windowDays, requests }: { windowDays: number; requests: ProductRequest[] }) {
  const notCarried = requests.filter((r) => !r.carried).slice(0, 5);
  const columns: readonly Column<ProductRequest>[] = [
    { id: 'request', header: 'Requested', priority: 1, render: (r) => r.request },
    { id: 'requesters', header: 'People', priority: 1, align: 'right', render: (r) => r.requesters },
    {
      id: 'carried',
      header: 'In the catalogue',
      priority: 2,
      render: (r) => (r.carried ? <Pill tone="ok">{r.sku ?? 'yes'}</Pill> : <Pill tone="neutral">not carried</Pill>),
    },
    { id: 'last', header: 'Most recent', priority: 3, mono: true, render: (r) => String(r.last_seen).slice(0, 10) },
  ];

  return (
    <Card title={`Feedback · product requests · ${windowDays} days`} basis="ledger">
      <DataTable
        columns={columns}
        rows={requests.slice(0, 12)}
        rowKey={(r) => r.request}
        caption="Product requests from the post-checkout survey"
        emptyState={<EmptyState message="No product requests in this window." />}
      />
      {notCarried.length > 0 ? (
        <p className="o-muted">
          Top requested and not carried: {notCarried.map((r) => r.request).join(', ')}. Names come
          from the survey verbatim; the requesters themselves are on the Customers page, not here.
        </p>
      ) : null}
    </Card>
  );
}

/* ------------------------------------------------------------------ */

export function AnnotationList({ annotations }: { annotations: Annotation[] }) {
  const columns: readonly Column<Annotation>[] = [
    { id: 'when', header: 'When', priority: 1, mono: true, render: (a) => (a.end_date ? `${a.start_date} to ${a.end_date}` : a.start_date) },
    { id: 'kind', header: 'Kind', priority: 2, render: (a) => <Pill tone={a.auto ? 'neutral' : 'info'}>{a.kind.replace(/_/g, ' ')}</Pill> },
    { id: 'title', header: 'What happened', priority: 1, render: (a) => a.title },
    { id: 'detail', header: 'Detail', priority: 4, render: (a) => a.detail ?? '' },
  ];

  return (
    <Card title="Annotations" basis="ledger">
      <DataTable
        columns={columns}
        rows={annotations}
        rowKey={(a) => a.annotation_id}
        caption="Events marked on the revenue and spend charts"
        emptyState={<EmptyState message="Nothing marked in this window." />}
      />
      <p className="o-muted">
        Promo windows, BOGO flips, stockouts and campaign launches become markers on every revenue
        chart in the product. A window, not a point, so a chart shows how long something lasted.
      </p>
    </Card>
  );
}
